import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */

export const _frontmatter = {
  "pageTitle": "Checkbox API",
  "title": "Checkbox",
  "props": ["CheckboxProps", "IndeterminateCheckboxProps"]
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const PageContent = makeShortcode("PageContent");
const LeadParagraph = makeShortcode("LeadParagraph");
const Link = makeShortcode("Link");
const Alert = makeShortcode("Alert");
const CheckboxProps = makeShortcode("CheckboxProps");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <PageContent componentName="checkbox" type="api" mdxType="PageContent">
      <LeadParagraph mdxType="LeadParagraph">
  Checkboxes are form elements used to select one or multiple items from a list.
      </LeadParagraph>
      <h2>{`Basic Usage`}</h2>
      <p>{`Related checkboxes should be grouped within the `}<Link to="/api/formgroup" mdxType="Link">{`Form Group`}</Link>{` control.`}</p>
      <p>{`The label/checkbox association will be handled via the passed in `}<inlineCode parentName="p">{`id`}</inlineCode>{` if one is supplied or via an auto-generated `}<inlineCode parentName="p">{`id`}</inlineCode>{` if not supplied.`}</p>
      <p>{`Checkboxes can either be controlled or uncontrolled. If you pass in a `}<inlineCode parentName="p">{`checked`}</inlineCode>{` prop, it is a controlled input. That means that to change the checkbox's state, you must updated the `}<inlineCode parentName="p">{`checked`}</inlineCode>{` prop's value.
If you do not want to have your checkbox be in a controlled state, but need the initial checked value to be set, pass in the `}<inlineCode parentName="p">{`defaultChecked`}</inlineCode>{` prop with the value you would like on initial render.`}</p>
      <Link to="/api/indeterminate" mdxType="Link">Indeterminate Checkboxes</Link> are similar to
standard checkboxes but with three available statuses.
      <p>{`See also: `}<Link to="/api/radio" mdxType="Link">{`Radio Buttons`}</Link>{` and `}<Link to="/api/toggle" mdxType="Link">{`Toggle Switches`}</Link>{`.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Checkbox, FormGroup } from 'react-magma-dom';

export function Example() {
  const [checked, updateChecked] = React.useState<boolean>(false);

  function handleUpdateChecked() {
    updateChecked(!checked);
  }

  return (
    <FormGroup labelText="Choose one or more">
      <Checkbox
        defaultChecked
        id="customId"
        labelText="Uncontrolled checkbox"
      />

      <Checkbox
        checked={checked}
        labelText="Controlled checkbox"
        onChange={handleUpdateChecked}
      />

      <Checkbox labelText="Checkbox label is really long and can wrap to multiple lines lorem ipsum dolar sit amet is really long and can wrap to multiple lines" />
    </FormGroup>
  );
}
`}</code></pre>
      <h2>{`Colors`}</h2>
      <p>{`By default, the checkboxes use the theme's primary color. But any other color may be passed in.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Checkbox, FormGroup, magma } from 'react-magma-dom';

export function Example() {
  return (
    <FormGroup labelText="Choose one or more colors">
      <Checkbox checked labelText="Default color" />
      <Checkbox
        checked
        color={magma.colors.success}
        labelText="Success color"
      />
      <Checkbox checked color={magma.colors.danger} labelText="Danger color" />
      <Checkbox
        checked
        color={magma.colors.warning}
        labelText="Warning color"
      />
      <Checkbox checked color={magma.colors.info} labelText="Info color" />
    </FormGroup>
  );
}
`}</code></pre>
      <h2>{`Disabled`}</h2>
      <p>{`Adding the optional boolean prop `}<inlineCode parentName="p">{`disabled`}</inlineCode>{` will apply disabled styles and add the disabled attribute to the underlying HTML element.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  Checkbox,
  FormGroup,
  IndeterminateCheckbox,
  magma,
} from 'react-magma-dom';

export function Example() {
  return (
    <FormGroup labelText="Choose one or more">
      <Checkbox
        color={magma.colors.success}
        disabled
        labelText="Disabled checkbox"
      />
      <Checkbox
        checked
        color={magma.colors.success}
        disabled
        labelText="Disabled checked checkbox"
      />
      <IndeterminateCheckbox
        color={magma.colors.success}
        disabled
        labelText="Disabled indeterminate checkbox"
        status="indeterminate"
      />
    </FormGroup>
  );
}
`}</code></pre>
      <h2>{`Inverse`}</h2>
      <p><inlineCode parentName="p">{`isInverse`}</inlineCode>{` is an optional boolean prop, that may be passed in when the component is to be displayed on a dark background.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  Card,
  CardBody,
  Checkbox,
  FormGroup,
  IndeterminateCheckbox,
  IndeterminateCheckboxStatus,
} from 'react-magma-dom';

export function Example() {
  return (
    <Card isInverse>
      <CardBody>
        <FormGroup isInverse labelText="Choose inverse options">
          <Checkbox isInverse labelText="Inverse" />
          <Checkbox checked isInverse labelText="Inverse checked" />
          <IndeterminateCheckbox
            status={IndeterminateCheckboxStatus.indeterminate}
            isInverse
            labelText="Indeterminate inverse"
          />
          <Checkbox disabled isInverse labelText="Disabled inverse" />
          <Checkbox
            checked
            disabled
            isInverse
            labelText="Disabled checked inverse"
          />
          <IndeterminateCheckbox
            disabled
            isInverse
            labelText="Disabled indeterminate inverse"
            status={IndeterminateCheckboxStatus.indeterminate}
          />
          <Checkbox
            errorMessage="Please check this box"
            isInverse
            labelText="Checkbox with error"
          />
        </FormGroup>
      </CardBody>
    </Card>
  );
}
`}</code></pre>
      <h2>{`Text Position`}</h2>
      <p>{`Use the optional prop `}<inlineCode parentName="p">{`textPosition`}</inlineCode>{` to position the label text on either side of the checkbox.
The `}<inlineCode parentName="p">{`textPosition`}</inlineCode>{` property takes the values `}<inlineCode parentName="p">{`left`}</inlineCode>{` and `}<inlineCode parentName="p">{`right`}</inlineCode>{`. The default value is `}<inlineCode parentName="p">{`right`}</inlineCode>{`.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Checkbox, CheckboxTextPosition, FormGroup } from 'react-magma-dom';

export function Example() {
  return (
    <FormGroup labelText="Text positions">
      <Checkbox
        labelText="Text left"
        textPosition={CheckboxTextPosition.left}
      />
      <Checkbox labelText="Text right" />
    </FormGroup>
  );
}
`}</code></pre>
      <h2>{`Error at Group Level`}</h2>
      <p>{`The `}<inlineCode parentName="p">{`errorMessage`}</inlineCode>{` prop can be used on the `}<inlineCode parentName="p">{`FormGroup`}</inlineCode>{` component to display an error message for the group. Each checkbox within the group will display with error styles.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Checkbox, FormGroup } from 'react-magma-dom';

interface CheckboxState {
  yellow: boolean;
  red: boolean;
  green: boolean;
}

export function Example() {
  const [hasError, setHasError] = React.useState<boolean>(true);
  const [state, setState] = React.useState<CheckboxState>({
    yellow: false,
    red: false,
    green: false,
  });

  const handleChange = (event: React.Change) => {
    setState({ ...state, [event.target.value]: event.target.checked });
  };

  React.useEffect(() => {
    setHasError(!state.yellow && !state.red && !state.green);
  }, [state]);

  return (
    <>
      <p>* indicates required fields</p>
      <FormGroup
        labelText="Choose one or more colors *"
        errorMessage={hasError ? 'Please choose at least one color' : null}
      >
        <Checkbox
          checked={state.yellow}
          labelText="Yellow 1"
          name="color"
          onChange={handleChange}
          value="yellow"
        />
        <Checkbox
          checked={state.red}
          labelText="Red"
          name="color"
          onChange={handleChange}
          value="red"
        />
        <Checkbox
          checked={state.green}
          labelText="Green"
          name="color"
          onChange={handleChange}
          value="green"
        />
      </FormGroup>
    </>
  );
}
`}</code></pre>
      <h2>{`Error at Checkbox Level`}</h2>
      <p>{`An `}<inlineCode parentName="p">{`errorMessage`}</inlineCode>{` can also be supplied to an individual checkbox, without the use of a `}<inlineCode parentName="p">{`FormGroup`}</inlineCode>{`.`}</p>
      <p>{`The `}<inlineCode parentName="p">{`required`}</inlineCode>{` attribute can be used on a single checkbox if that individual checkbox is required.
An example of when to use this would be for a terms and conditions checkbox.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Checkbox } from 'react-magma-dom';

export function Example() {
  const [hasError, setHasError] = React.useState<boolean>(true);

  function handleChange(event: React.ChangeEventHandler) {
    setHasError(!event.target.checked);
  }

  return (
    <Checkbox
      errorMessage={
        hasError ? 'You must agree to the terms and conditions' : null
      }
      labelText="I agree to the terms and conditions"
      onChange={handleChange}
      required
    />
  );
}
`}</code></pre>
      <h2>{`Visually Hidden Label`}</h2>
      <p>{`Adding the `}<inlineCode parentName="p">{`isTextVisuallyHidden`}</inlineCode>{` prop will make the label text available to screen readers, but will hide it visually.`}</p>
      <Alert variant="warning" mdxType="Alert">
  Use caution when using the <inlineCode>isTextVisuallyHidden</inlineCode> prop;
  it should only be used in rare circumstances when the purpose of the checkbox
  is conveyed in another way. While screen readers will announce the label when
  the checkbox gains focus, it is still best to have a visual label. This is
  especially helpful for individuals with cognitive disabilities and users who
  have fine motor control limitations.
      </Alert>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Checkbox } from 'react-magma-dom';

export function Example() {
  return <Checkbox labelText="Hidden checkbox" isTextVisuallyHidden />;
}
`}</code></pre>
      <h2>{`Custom Styles`}</h2>
      <p>{`Custom styles can be passed into the Checkbox component. The `}<inlineCode parentName="p">{`containerStyle`}</inlineCode>{` property will apply to the container.
Additional `}<inlineCode parentName="p">{`labelStyle`}</inlineCode>{` and `}<inlineCode parentName="p">{`inputStyle`}</inlineCode>{` properties are available to style the respective elements. Please use discretion when adding custom styles.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Checkbox } from 'react-magma-dom';

export function Example() {
  return (
    <>
      <Checkbox
        checked
        containerStyle={{ border: '1px dotted purple' }}
        inputStyle={{ boxShadow: '3px 3px 3px rgba(0, 0 , 0, .6)' }}
        labelStyle={{ color: 'brown', fontStyle: 'italic' }}
        labelText="Custom styles"
      />

      <Checkbox checked labelText="No custom styles" />
    </>
  );
}
`}</code></pre>
      <h2>{`Node as labelText`}</h2>
      <p>{`The `}<inlineCode parentName="p">{`labelText`}</inlineCode>{` prop can either be a string, or it can be a React Node.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Checkbox } from 'react-magma-dom';

export function Example() {
  const myLabel = (
    <strong>
      Node as <i>label</i>
    </strong>
  );

  return <Checkbox labelText={myLabel} />;
}
`}</code></pre>
      <h2>{`Internationalization`}</h2>
      <p>{`Some of the internationalization overrides use placeholders to insert selected values in to the message.
Placeholders are specific keywords surrounded by curly braces.`}</p>
      <ul>
        <li parentName="ul"><inlineCode parentName="li">{`{labelText}`}</inlineCode>{` will be replaced with the checkbox's `}<inlineCode parentName="li">{`labelText`}</inlineCode>{`.`}</li>
      </ul>
      <p>{`Full example of `}<Link to="/api/internationalization" mdxType="Link">{`internationalization override options`}</Link></p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  Checkbox,
  defaultI18n,
  IndeterminateCheckbox,
  I18nContext,
  IndeterminateCheckboxStatus,
  magma,
} from 'react-magma-dom';

export function Example() {
  const [checkedItems, setCheckedItems] = React.useState<Array<boolean>>([
    true,
    false,
  ]);

  const status: IndeterminateCheckboxStatus = checkedItems.every(Boolean)
    ? IndeterminateCheckboxStatus.checked
    : checkedItems.some(Boolean)
      ? IndeterminateCheckboxStatus.indeterminate
      : IndeterminateCheckboxStatus.unchecked;

  function handleUpdateIndeterminateChecked(
    event: React.ChangeEvent<HTMLInputElement>
  ) {
    setCheckedItems([event.target.checked, event.target.checked]);
  }

  function handleUpdateRedChecked(event: React.ChangeEvent<HTMLInputElement>) {
    setCheckedItems([event.target.checked, checkedItems[1]]);
  }

  function handleUpdateBlueChecked(event: React.ChangeEvent<HTMLInputElement>) {
    setCheckedItems([checkedItems[0], event.target.checked]);
  }

  return (
    <I18nContext.Provider
      value={{
        ...defaultI18n,
        indeterminateCheckbox: {
          isCheckedAnnounce:
            'Custom message about checked status for {labelText} checkbox',
          isIndeterminateAnnounce:
            'Custom message about indeterminate status for {labelText} checkbox',
          isUncheckedAnnounce:
            'Custom message about unchecked status for {labelText} checkbox',
        },
      }}
    >
      <IndeterminateCheckbox
        onChange={handleUpdateIndeterminateChecked}
        status={status}
        labelText="Colors"
      />
      <div style={{ marginLeft: magma.spaceScale.spacing08 }}>
        <Checkbox
          checked={checkedItems[0]}
          onChange={handleUpdateRedChecked}
          labelText="Red"
        />
        <Checkbox
          checked={checkedItems[1]}
          onChange={handleUpdateBlueChecked}
          labelText="Blue"
        />
      </div>
    </I18nContext.Provider>
  );
}
`}</code></pre>
      <h2>{`Testing`}</h2>
      <p>{`Passing in the `}<inlineCode parentName="p">{`testId`}</inlineCode>{` prop will add the `}<inlineCode parentName="p">{`data-testid`}</inlineCode>{` attribute to the input element for easier querying in tests.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-html",
          "metastring": "noCodeSandbox",
          "noCodeSandbox": true
        }}>{`<Checkbox
  defaultChecked="{true}"
  id="customId"
  labelText="Checkbox"
  testId="checkbox-id"
/>
`}</code></pre>
      <h2>{`Checkbox Props`}</h2>
      <p><strong parentName="p">{`This component uses `}<inlineCode parentName="strong">{`forwardRef`}</inlineCode>{`. The ref is applied to the Checkbox `}<inlineCode parentName="strong">{`input`}</inlineCode>{` element.`}</strong></p>
      <p>{`All of the `}<a parentName="p" {...{
          "href": "https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input"
        }}>{`standard input attributes`}</a>{` can be provided as props and will be applied to the `}<inlineCode parentName="p">{`input`}</inlineCode>{` element that gets rendered.`}</p>
      <CheckboxProps mdxType="CheckboxProps" />
    </PageContent>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      